/**
 * Usuarios del SSO: invitacion, suspension, reseteo de clave y asignacion de roles.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '@presentation/components/common';
import { RequireRole } from '@presentation/router/RequireRole';
import { useUsers } from '@presentation/hooks';
import { SYSTEM_ROLES, USER_STATUS_LABELS, USER_TYPE_LABELS } from '@shared/constants';
import type { SsoUser, UserType } from '@domain/entities';
import type { ResetPasswordResult } from '@application/dto';
import { UserRolesPanel } from './UserRolesPanel';
import { TemporaryPasswordPanel } from './TemporaryPasswordPanel';
import styles from '../admin.module.css';

const emptyInvite = {
  email: '',
  givenName: '',
  familyName: '',
  userType: 'INTERNAL' as UserType,
};

export function UsersPage() {
  const [search, setSearch] = useState('');
  const { users, isLoading, error, invite, toggleStatus, resetPassword } = useUsers(search);
  const [form, setForm] = useState(emptyInvite);
  const [selected, setSelected] = useState<SsoUser | null>(null);
  const [resetResult, setResetResult] = useState<ResetPasswordResult | null>(null);

  const handleResetPassword = async (user: SsoUser) => {
    const confirmed = window.confirm(
      `Se va a generar una clave temporal para ${user.email} y se cerrarán sus sesiones abiertas. ¿Continuar?`
    );
    if (!confirmed) return;

    setResetResult(await resetPassword(user));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (await invite(form)) {
      setForm(emptyInvite);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Usuarios</h1>
          <p className={styles.subtitle}>
            La invitacion crea la identidad en el proveedor y manda el mail con la clave temporal.
          </p>
        </div>
      </header>

      <div className={styles.toolbar}>
        <input
          className={styles.input}
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Buscar por email, nombre o apellido"
          aria-label="Buscar usuarios"
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <RequireRole roles={[SYSTEM_ROLES.admin]}>
        <form className={styles.form} onSubmit={submit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              value={form.email}
              onChange={event => setForm({ ...form, email: event.target.value })}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="givenName">
              Nombre
            </label>
            <input
              id="givenName"
              className={styles.input}
              value={form.givenName}
              onChange={event => setForm({ ...form, givenName: event.target.value })}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="familyName">
              Apellido
            </label>
            <input
              id="familyName"
              className={styles.input}
              value={form.familyName}
              onChange={event => setForm({ ...form, familyName: event.target.value })}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="userType">
              Tipo
            </label>
            <select
              id="userType"
              className={styles.select}
              value={form.userType}
              onChange={event => setForm({ ...form, userType: event.target.value as UserType })}
            >
              <option value="INTERNAL">{USER_TYPE_LABELS.INTERNAL}</option>
              <option value="EXTERNAL">{USER_TYPE_LABELS.EXTERNAL}</option>
            </select>
          </div>

          <div className={styles.actions}>
            <Button type="submit">Invitar</Button>
          </div>
        </form>
      </RequireRole>

      {resetResult && (
        <TemporaryPasswordPanel result={resetResult} onClose={() => setResetResult(null)} />
      )}

      {selected && <UserRolesPanel user={selected} onClose={() => setSelected(null)} />}

      {isLoading ? (
        <p className={styles.empty}>Cargando usuarios...</p>
      ) : users.length === 0 ? (
        <p className={styles.empty}>No hay usuarios que coincidan.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Ultimo ingreso</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.fullName}</td>
                <td>{USER_TYPE_LABELS[user.userType]}</td>
                <td>
                  <span
                    className={`${styles.badge} ${
                      user.status === 'ACTIVE'
                        ? styles.badgeActive
                        : user.status === 'SUSPENDED'
                          ? styles.badgeInactive
                          : styles.badgeNeutral
                    }`}
                  >
                    {USER_STATUS_LABELS[user.status]}
                  </span>
                </td>
                <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</td>
                <td>
                  <div className={styles.actions}>
                    <Button size="small" variant="secondary" onClick={() => setSelected(user)}>
                      Roles
                    </Button>
                    <RequireRole roles={[SYSTEM_ROLES.admin]}>
                      <Button
                        size="small"
                        variant={user.status === 'SUSPENDED' ? 'primary' : 'danger'}
                        onClick={() => void toggleStatus(user)}
                      >
                        {user.status === 'SUSPENDED' ? 'Reactivar' : 'Suspender'}
                      </Button>
                      {user.status !== 'SUSPENDED' && (
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => void handleResetPassword(user)}
                        >
                          Resetear clave
                        </Button>
                      )}
                    </RequireRole>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
